import { upperFirst } from 'lodash';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

export function useStateObject<T extends object>(initialState: T) {
  const [state, setState] = useState(initialState);
  const _actions = useMemo(() => _createActionsInternal(state, setState), []);
  return {
    state,
    actions: {
      ..._actions,
      set: (state: SetStateAction<T> | Partial<T>) => {
        setState(
          typeof state === 'function'
            ? state
            : (prevState) => ({
                ...prevState,
                ...state,
              }),
        );
      },
    },
  };
}

function _createActionsInternal<T extends object, K extends keyof T>(
  state: T,
  setState: Dispatch<SetStateAction<T>> = () => {},
): InternalActions<T> {
  const _actions = Object.keys(state).reduce<Partial<InternalActions<T>>>(
    (acc, key) => ({
      ...acc,
      [`set${upperFirst(key)}`]: (value: K) =>
        setState((prevState) => ({
          ...prevState,
          [key]: value,
        })),
    }),
    {},
  );
  return _actions as unknown as InternalActions<T>;
}

type InternalActions<T> = {
  [Property in keyof T as `set${Capitalize<Property & string>}`]: (
    value: T[Property],
  ) => void;
};
